import { useEffect, useRef } from 'react';

import { TUseComposerAutoFocusParams } from '@/components/molecule/ChatInput/types';

const DIALOG_SELECTOR = '[role="dialog"]';

/**
 * Owns keyboard focus for the composer. Places the caret in the textarea once, the first time the
 * composer is usable and free to take focus, and hands the caret to the textarea when a send starts
 * from the send button. Focus is claimed only when nothing else holds it and no modal dialog is open,
 * so the composer never pulls focus away from the user or out of a dialog's focus trap. The composer
 * is read-only rather than disabled while an answer is in flight, so focus is never stranded on the
 * document body.
 */
const useComposerAutoFocus = ({ textAreaRef, sendButtonRef, isDisabled }: TUseComposerAutoFocusParams) => {
  const hasClaimedFocusRef = useRef(false);
  const wasBlockedByDialogRef = useRef(false);

  useEffect(() => {
    if (hasClaimedFocusRef.current || isDisabled) return;

    /**
     * Moves the caret into the composer unless a dialog is open or something else holds focus.
     * Returns whether focus was claimed.
     */
    const claimFocus = (): boolean => {
      if (hasClaimedFocusRef.current) return false;

      if (document.querySelector(DIALOG_SELECTOR) !== null) {
        wasBlockedByDialogRef.current = true;
        return false;
      }

      const isFocusUnclaimed = document.activeElement === null || document.activeElement === document.body;
      if (!isFocusUnclaimed) return false;

      hasClaimedFocusRef.current = true;
      textAreaRef.current?.focus();
      return true;
    };

    if (claimFocus() || !wasBlockedByDialogRef.current) return;

    // INFO: The chat page can mount behind a modal (release notes, onboarding) that owns focus and
    // stays in the DOM until its closing animation ends, so the arrival focus waits for the dialog
    // to be removed. Watching stops as soon as the caret is placed.
    const observer = new MutationObserver(() => {
      if (claimFocus()) observer.disconnect();
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
    // INFO: isDisabled is the only trigger — the chat page can mount while a thread is still being
    // restored, so the arrival focus waits until the composer accepts input. textAreaRef is a stable
    // ref object and never changes identity.
  }, [isDisabled, textAreaRef]);

  useEffect(() => {
    if (!isDisabled) return;

    // INFO: The send button turns inoperable for the duration of the request, so the caret is handed
    // to the composer as the send starts — it stays focusable while busy (read-only, not disabled),
    // which keeps the focus ring off a control that cannot be used and leaves the user where they
    // continue typing. Focus anywhere else belongs to the user and is left untouched.
    if (document.activeElement === sendButtonRef.current) textAreaRef.current?.focus();
    // INFO: isDisabled is the only trigger — it turns true exactly when the question is sent. Both
    // refs are stable ref objects and never change identity.
  }, [isDisabled, textAreaRef, sendButtonRef]);
};

export { useComposerAutoFocus };
